// components/OnboardingModal.jsx
import { Dialog, DialogContent } from "../arc/components/dialog/dialog";

const DM_SANS = "'DM Sans', system-ui, sans-serif";
const DM_MONO = "'DM Mono', 'Fira Code', monospace";
const NAV_DARK = "#0c2c54";

export default function OnboardingModal({ onDismiss }) {
  return (
    <Dialog open onOpenChange={(next) => { if (!next) onDismiss?.(); }}>
      <DialogContent className="onboarding-dialog" title="Welcome to AXO Signal.">
        <style>{`
          @keyframes axoPulse { 0%,100%{ opacity:1; box-shadow:0 0 6px rgba(52,211,153,0.6) } 50%{ opacity:.7; box-shadow:0 0 12px rgba(52,211,153,0.3) } }
          .onboarding-dialog { background: linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%); border-color: rgba(131,148,171,0.2); box-shadow: 0 32px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(131,148,171,0.12); }
          .onboarding-dialog h2 { color: #ffffff; font-size: 28px; font-weight: 700; letter-spacing: -0.02em; line-height: 1.2; }
          .onboarding-dialog > div:first-child { border-bottom: 1px solid rgba(131,148,171,0.2); }
          .onboarding-dialog > div:first-child button { color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.18); }
          .axo-dismiss-btn:hover { background: #ffffff !important; transform: translateY(-1px); }
          .axo-dismiss-btn:active { transform: translateY(0); }
          .axo-link:hover { color: rgba(255,255,255,0.95) !important; }
        `}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Status pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: "fit-content", background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.22)", borderRadius: 20, padding: "5px 12px 5px 9px" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#34d399", flexShrink: 0, animation: "axoPulse 2s infinite", display: "block" }} />
            <span style={{ fontFamily: DM_MONO, fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", color: "#34d399", textTransform: "uppercase" }}>
              Account active
            </span>
          </div>

          <p style={{ fontFamily: DM_MONO, fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, margin: 0 }}>
            Your account is now active and ready to use.
          </p>

          <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(131,148,171,0.25), transparent)" }} />

          {/* Feedback & Support */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" fill="none" stroke="#818cf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <p style={{ fontFamily: DM_MONO, fontSize: 11, fontWeight: 500, color: "#818cf8", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>
                Feedback &amp; Support
              </p>
              <p style={{ fontFamily: DM_SANS, fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.7, margin: 0 }}>
                We highly encourage feedback, feature ideas, workflow suggestions, and data
                observations as we continue enhancing the platform. If you identify opportunities
                for improvement or require bespoke functionality, please contact us via the support
                chat or email{" "}
                <a href="mailto:support@axosignal.io" className="axo-link" style={{ color: "rgba(255,255,255,0.7)", textDecoration: "underline", textUnderlineOffset: 3, transition: "color 0.15s" }}>
                  support@axosignal.io
                </a>.
              </p>
            </div>
          </div>

          {/* Best Experience */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
              </svg>
            </div>
            <div>
              <p style={{ fontFamily: DM_MONO, fontSize: 11, fontWeight: 500, color: "#fbbf24", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>
                Best Experience
              </p>
              <p style={{ fontFamily: DM_SANS, fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.7, margin: 0 }}>
                For the best experience, we currently recommend using AXO Signal via laptop or
                desktop browser while the mobile app is finalised.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div style={{ paddingTop: 16, borderTop: "1px solid rgba(131,148,171,0.15)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span style={{ fontFamily: DM_MONO, fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: "0.04em" }}>
              platform@axosignal.io
            </span>
            <button
              onClick={() => onDismiss?.()}
              className="axo-dismiss-btn"
              style={{ fontFamily: DM_SANS, fontSize: 13, fontWeight: 600, color: NAV_DARK, background: "#f0ede8", border: "none", borderRadius: 8, padding: "10px 22px", cursor: "pointer", transition: "all 0.15s" }}
            >
              Get started →
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
